import type { TreeNodeData } from "../types";

const getAllDescendantKeys = (node: TreeNodeData): string[] => {
  const keys: string[] = [];
  const walk = (node: TreeNodeData) => {
    keys.push(node.key);
    node.children?.forEach(walk);
  };
  walk(node);

  return keys;
};

const computeAncestorStates = (
  nodes: TreeNodeData[],
  checked: Set<string>,
  halfChecked: Set<string>
): void => {
  nodes.forEach(node => {
    if (!node.children?.length) return;

    computeAncestorStates(node.children, checked, halfChecked);

    const childKeys = node.children.map(child => child.key);
    const checkedCount = childKeys.filter(key => checked.has(key)).length;
    const halfCount = childKeys.filter(key => halfChecked.has(key)).length;

    if (checkedCount === childKeys.length) {
      checked.add(node.key);
      halfChecked.delete(node.key);
    } else if (checkedCount > 0 || halfCount > 0) {
      checked.delete(node.key);
      halfChecked.add(node.key);
    } else {
      checked.delete(node.key);
      halfChecked.delete(node.key);
    }
  });
};

export const toggleCheckCascade = (
  key: string,
  isChecking: boolean,
  treeData: TreeNodeData[],
  currentChecked: Set<string>
): { checked: string[]; halfChecked: string[] } => {
  const checked = new Set(currentChecked);
  const halfChecked = new Set<string>();

  const findAndProcess = (nodes: TreeNodeData[]): boolean => {
    for (const node of nodes) {
      if (node.key === key) {
        const descendantKeys = getAllDescendantKeys(node);
        descendantKeys.forEach(descendantKey =>
          isChecking
            ? checked.add(descendantKey)
            : checked.delete(descendantKey)
        );

        return true;
      }
      if (node.children && findAndProcess(node.children)) return true;
    }

    return false;
  };

  findAndProcess(treeData);
  computeAncestorStates(treeData, checked, halfChecked);

  return {
    checked: Array.from(checked),
    halfChecked: Array.from(halfChecked),
  };
};
